import * as React from 'react';
import { useSelector } from 'react-redux';
import { Typography, TextInput, Button, Box, Flex, color } from '@kistech/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors, actions as authActions } from 'store/auth';
import { Admin } from 'store/admins';
import { KioskControlCenter } from 'components/KioskControlCenter';
import { RouteComponentProps, Redirect } from 'react-router';
import { ValidPasswordToolTip } from '../../components/ValidPasswordToolTip';
import { validationSchema } from '../../components/CreateNewUserOverlay/utils';
import { useStyles } from './styled';
import { Grid, Typography as MuiTypography, Box as MuiBox } from '@mui/material';

const INVALID_EMAIL = 'Invalid email format';

interface RouteProps {
  token?: string;
}

const ResetPasswordView: React.FC<RouteComponentProps<RouteProps>> = (props: RouteComponentProps<RouteProps>) => {
  // ACTION CREATORS AND SELECTORS
  const { updateAdmin, resetAction, resetPassword } = useActions({
    updateAdmin: authActions.updateCurrentAdmin,
    resetAction: authActions.resetAction,
    resetPassword: authActions.resetPassword
  });
  const isAuthenticated = useSelector(authSelectors.selectIsAuthenticated);
  const admin = useSelector(authSelectors.selectAdmin) as Admin;
  const error = useSelector(authSelectors.selectActionError);
  const updating = useSelector(authSelectors.selectActionPending);
  const updateSuccess = useSelector(authSelectors.selectActionSuccessful);
  const { token } = props.match.params;
  const classes = useStyles();

  // EMAIL AND PASSWORD INPUT STATES
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');

  // INVALID MESSAGE WHEN EMAIL INCORRECT FORMAT
  const [invalidMsg, setInvalidMsg] = React.useState<string | null>(null);

  const passwordsMismatch = !password || !!invalidMsg || password !== confirmPassword;
  const [showPasswordInfo, setPasswordInfoVisibility] = React.useState(false);

  // ON UPDATE OF ERROR - SET THE ERROR AS INVALID MESSAGE
  React.useEffect(() => {
    setInvalidMsg(error);
  }, [error]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EMAIL VALIDATION
  const passwordValidationFn = async () => {
    // IF THERE WAS ERROR MESSAGE - SET IT TO INVALID MESSAGE
    if (passwordsMismatch) {
      setInvalidMsg('Passwords do not match');
    } else {
      let invalidMessage = null;

      // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
      if (password) {
        const result = await validationSchema.validate({ password }).catch((err) => err);
        invalidMessage = result && result.message;
      }

      setInvalidMsg(invalidMessage);
    }

    return null;
  };

  // INPUT CHANGE
  const onInputChange = (key: string, value: string) => {
    // IF INVALID MESSAGE EXISTS SET IT TO NULL
    if (invalidMsg) setInvalidMsg(null);

    if (key === 'password') {
      setPasswordInfoVisibility(true);
      setPassword(value);
    } else if (key === 'confirmPassword') {
      setConfirmPassword(value);
    }
  };

  // FORM INPUT BLUR
  const onInputBlur = async (key: string, value: string | null = null) => {
    if (key === 'password') {
      setPasswordInfoVisibility(false);
    }
    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await validationSchema.validate({ [key]: value && value.toString().trim() }).catch((err) => err);
      invalidMessage = result && result.message;
    }

    setInvalidMsg(invalidMessage);
  };

  // FORM SUBMIT
  const onSaveClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // FIRES UPDATE ADMIN ASYNC FN
    if (token) resetPassword({ token, password });
    else updateAdmin({ password });
  };

  const onControlCenterClick = () => {
    resetAction();
    props.history.push('/clients');
  };

  if (!isAuthenticated && !token) return <Redirect to="/" />;

  const FirstTimeContent = () => (
    <>
      <MuiTypography className={classes.resetPasswordHeader}>
        Welcome
        <br /> {admin.name}!
      </MuiTypography>
      <MuiBox className={classes.resetPasswordTextBorder} />
      <MuiTypography className={classes.resetPasswordSubHeader}>
        This appears to be your first time logging into the KIS Control Center. Please take the time to update your
        password to one that is secure and easy to remember.
      </MuiTypography>
    </>
  );

  const ResetContent = () => (
    <>
      <MuiTypography className={classes.resetPasswordHeader}>Reset your password</MuiTypography>
      <MuiBox className={classes.resetPasswordTextBorder} />
      <MuiTypography className={classes.resetPasswordSubHeader}>Please enter a new password.</MuiTypography>
    </>
  );

  return (
    <Grid container direction="row" className={classes.resetPasswordOuterContainer}>
      {/* LEFT OF CARD */}
      <Grid item md={6} xl={6} lg={6} sm={12} xs={12} className={classes.resetPasswordImageBlock}>
        <Flex fullHeight bg="light-gray">
          <KioskControlCenter />
        </Flex>
      </Grid>
      {/* RIGHT OF CARD - LOGIN FORM */}
      {!updateSuccess && (
        <Grid direction="column" item md={6} xl={6} lg={6} sm={12} xs={12} className={classes.resetPasswordViewBlock}>
          <MuiBox className={classes.resetPasswordTextBox}>{token ? <ResetContent /> : <FirstTimeContent />}</MuiBox>
          <form>
            {/* PASSWORD INPUT */}
            <MuiBox className={classes.resetPasswordInput}>
              <TextInput
                onChange={(e) => onInputChange('password', e.target.value)}
                isError={!!invalidMsg}
                label="New Password"
                type="password"
                placeholder="New Password"
                fullWidth
                onBlur={(e) => onInputBlur('password', e.target.value.trim())}
                onFocus={() => setPasswordInfoVisibility(true)}
                endAdornment={<ValidPasswordToolTip showValidPasswordInfo={showPasswordInfo} value={password} />}
              />
            </MuiBox>
            {/* CONFIRM PASSWORD INPUT */}
            <MuiBox className={classes.resetConfirmPasswordInput}>
              <TextInput
                onChange={(e) => onInputChange('confirmPassword', e.target.value)}
                isError={!!invalidMsg && invalidMsg !== INVALID_EMAIL}
                label="Confirm Password"
                type="password"
                placeholder="Re-enter your password"
                validate={passwordValidationFn}
                fullWidth
              />
            </MuiBox>
            {/* ERROR MESSAGE */}
            {invalidMsg && !updating && (
              <Box flexOverride="none" margin="0 0 5" height={24}>
                <Typography size={16} color={color.RED}>
                  {invalidMsg}
                </Typography>
              </Box>
            )}
            {/* SUBMIT BUTTON */}
            <Button
              isDisabled={passwordsMismatch}
              size="large"
              onClick={onSaveClick}
              fullWidth
              isLoading={updating}
              type="submit"
              className={classes.resetPasswordSaveButton}
            >
              Save
            </Button>
          </form>
        </Grid>
      )}

      {updateSuccess && (
        <Grid
          direction="column"
          item
          md={6}
          xl={6}
          lg={6}
          sm={12}
          xs={12}
          className={classes.PasswordResetSuccessBlock}
        >
          <MuiBox className={classes.resetSuccessMessageBox}>
            <MuiTypography className={classes.resetSuccessHeader}>
              We&apos;ve successfully updated your password!
            </MuiTypography>
            <MuiBox className={classes.resetSuccessDivider} />
            <MuiTypography className={classes.resetSuccessSubHeader}>
              Thanks for keeping your profile secure. You may now use the KIS Control Center to manage your kiosks.
            </MuiTypography>
          </MuiBox>
          <Button
            size="large"
            onClick={onControlCenterClick}
            fullWidth
            to="/clients"
            className={classes.resetPasswordSaveButton}
          >
            {token ? 'Back to Sign In' : 'Control Center'}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default ResetPasswordView;
